import { debounce } from 'lodash/fp';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

export const useOverlayCarousel = ({
	items,
	gap,
	children,
}: {
	items: React.ReactNode[];
	gap?: number;
	children: ReactNode;
}) => {
	// Ref for the scroll container
	const containerRef = useRef<HTMLDivElement>(null);
	// State for the left offset of each item (relative to container)
	const [itemOffsets, setItemOffsets] = useState<number[]>([]);
	// State for the array of first item indices for each page
	const [pages, setPages] = useState<number[]>([]);
	// State for the current page index
	const [currentPage, setCurrentPage] = useState(0);
	// Ref to track if a scroll was triggered programmatically (dot/arrow)
	const isProgrammaticScroll = useRef(false);

	// Responsive gap (default: 6px mobile, 12px desktop)
	const isMobile = useMediaQuery({ maxWidth: 700 });
	const gapValue = gap ?? (isMobile ? 6 : 12);

	// Array of refs for each item
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
	if (itemRefs.current.length !== items.length) {
		itemRefs.current = Array(items.length).fill(null);
	}

	/**
	 * Measures the left offset of each carousel item and determines page breaks.
	 * Updates itemOffsets (array of left offsets) and pages (array of first item indices for each page).
	 * Called on mount, resize, and when children change.
	 */
	const measure = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;
		// Measure left offset of each item relative to the container
		const offsets = itemRefs.current.map(item => (item ? item.offsetLeft : 0));
		setItemOffsets(offsets);
		// Build pages: each page starts with the first item that fits in the container
		const newPages: number[] = [];
		let pageStart = 0;
		while (pageStart < items.length) {
			newPages.push(pageStart);
			let width = 0;
			let i = pageStart;
			// Add items to the page until the next item would overflow the container
			while (
				i < items.length &&
				width + (itemRefs.current[i]?.offsetWidth || 0) <= container.clientWidth
			) {
				width += (itemRefs.current[i]?.offsetWidth || 0) + gapValue;
				i++;
			}
			if (i === pageStart) i++; // always advance at least one item to avoid infinite loop
			pageStart = i;
		}
		setPages(newPages);
	}, [items.length, gapValue]);

	// Re-measure on mount, resize, or when children change
	useEffect(() => {
		measure();
		const container = containerRef.current;
		if (!container) return;
		const resizeObserver = new window.ResizeObserver(() => {
			measure();
		});
		resizeObserver.observe(container);
		return () => {
			resizeObserver.disconnect();
		};
	}, [measure, children]);

	/**
	 * Scrolls the carousel to the left offset of the first item of the selected page.
	 * @param pageIndex - The index of the page to scroll to.
	 * Sets currentPage to the selected page (forces last page highlight if at the end).
	 */
	const goToPage = useCallback(
		(pageIndex: number) => {
			const container = containerRef.current;
			if (!container || !pages.length) return;
			const itemIndex = pages[pageIndex];
			const left = itemOffsets[itemIndex] ?? 0;
			isProgrammaticScroll.current = true;
			container.scrollTo({ left, behavior: 'smooth' });
			// Force last page highlight if going to last page
			if (pageIndex === pages.length - 1) {
				setCurrentPage(pageIndex);
			} else {
				setCurrentPage(pageIndex);
			}
		},
		[pages, itemOffsets]
	);

	/**
	 * Updates the current page index based on the scroll position of the container.
	 * If at or past the last item's offset, forces last page highlight.
	 * Otherwise, finds the closest page whose first item is nearest to the scroll position.
	 * Always allows free manual scroll.
	 */
	const handleScroll = useCallback(() => {
		const container = containerRef.current;
		if (!container || !pages.length) return;
		if (isProgrammaticScroll.current) {
			isProgrammaticScroll.current = false;
			return;
		}
		const scrollLeft = container.scrollLeft;
		// If at or past the last item's offset, force last page
		const lastItemIndex = pages[pages.length - 1];
		const lastItemOffset = itemOffsets[lastItemIndex] ?? 0;
		if (scrollLeft >= lastItemOffset - 2) {
			// allow small fudge factor
			setCurrentPage(pages.length - 1);
			return;
		}
		// Otherwise, find closest page
		let closestPage = 0;
		let minDist = Infinity;
		for (let i = 0; i < pages.length; i++) {
			const itemIndex = pages[i];
			const offset = itemOffsets[itemIndex] ?? 0;
			const dist = Math.abs(scrollLeft - offset);
			if (dist < minDist) {
				minDist = dist;
				closestPage = i;
			}
		}
		setCurrentPage(closestPage);
	}, [pages, itemOffsets]);

	// Attach scroll handler (debounced) to the container
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const debounced = debounce(50, handleScroll);
		container.addEventListener('scroll', debounced);
		return () => container.removeEventListener('scroll', debounced);
	}, [handleScroll]);

	// Full width class names
	// Utility for full-width styling
	const fullWidthClassNames = [
		'w-screen',
		'px-150',
		'md:px-[calc((100vw-690px)/2)]',
		'lg:px-[calc((100vw-930px)/2)]',
		'xl:px-[calc((100vw-1530px)/2)]',
		'-mx-150',
		'md:mx-[calc((100vw-690px)/-2)]',
		'lg:mx-[calc((100vw-930px)/-2)]',
		'xl:mx-[calc((100vw-1530px)/-2)]',
	].join(' ');

	return {
		containerRef,
		itemRefs,
		pages,
		currentPage,
		goToPage,
		fullWidthClassNames,
	};
};
